import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Download, Sparkles, FileText } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorState from '../../components/ErrorState';
import * as reportsApi from '../../api/reports.api';
import { errorMessage } from '../../api/client';
import { formatDate, formatDateTime, fileSize } from '../../utils/format';

function ReportDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    reportsApi.getReport(id)
      .then(setReport)
      .catch((e) => setErr(errorMessage(e)))
      .finally(() => setLoading(false));
  }, [id]);

  async function onDownload() {
    try {
      await reportsApi.downloadReportFile(report._id, report.fileName);
    } catch (e) { toast.error(errorMessage(e)); }
  }

  if (loading) return <LoadingSpinner size="lg" />;
  if (err || !report) return <ErrorState message={err || 'Report not found'} action={<button className="btn" onClick={() => navigate(-1)}><ArrowLeft size={14} /> Back</button>} />;

  const isPdf = (report.fileType || '').includes('pdf');

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Reports', to: '/reports' }, { label: report.fileName }]}
        title={report.fileName}
        subtitle={`Uploaded ${formatDateTime(report.uploadedAt)}`}
        actions={
          <>
            <button className="btn btn-outline" onClick={onDownload}>
              <Download size={16} /> Download
            </button>
          </>
        }
      />

      <div className="grid grid-2">
        <div className="card">
          <div className="card-title">
            <h3>Details</h3>
            <StatusBadge status={report.status} />
          </div>
          <dl className="kvs">
            <dt>File</dt>
            <dd>
              <div className="row" style={{ gap: 8 }}>
                <FileText size={16} />
                <span className="cell-strong">{report.fileName}</span>
              </div>
              <div className="muted text-xs">{report.fileType} · {fileSize(report.fileSize)}</div>
            </dd>
            <dt>Package</dt><dd>{report.package?.name || '—'}</dd>
            <dt>Order</dt>
            <dd>
              {report.order?._id ? (
                <Link to={`/orders/${report.order._id}`}>{report.order.orderNumber}</Link>
              ) : '—'}
            </dd>
            <dt>Lab</dt><dd>{report.vendor?.name || '—'}</dd>
            <dt>Uploaded</dt><dd>{formatDateTime(report.uploadedAt)}</dd>
            {report.remarks && <><dt>Remarks</dt><dd>{report.remarks}</dd></>}
          </dl>
        </div>

        <div className="card">
          <div className="card-title">
            <h3>AI summary</h3>
            <span className="badge brand"><Sparkles size={12} /> Coming soon</span>
          </div>
          <div className="muted">
            AI-generated summaries to help you understand your report at a glance are coming soon.
          </div>
        </div>
      </div>

      {isPdf && (
        <div className="card" style={{ marginTop: 'var(--s-4)', padding: 0, overflow: 'hidden' }}>
          <iframe
            title={report.fileName}
            src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/v1'}/reports/${report._id}/file?inline=1`}
            style={{ width: '100%', height: '70vh', border: 0 }}
          />
          <div className="hint" style={{ padding: 'var(--s-3) var(--s-4)' }}>
            If the preview doesn't load, your browser may not be sending the auth cookie/header for iframes.
            Use the <strong>Download</strong> button above instead.
          </div>
        </div>
      )}
    </>
  );
}

export default ReportDetails;
